import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';
import { setAuthContextOnBackendService } from 'BackendAuthHandler';
import BackendService from 'BackendService';
import {
  CommonLoggedInData,
  CommonLoggedInDataContext,
  CommonLoggedInDataImpl,
  CommonLoggedInDataWithToken,
} from 'CommonLoggedInData';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildAuthRedirectUri, isNonAdminPublicRoute } from './nonAdminAuthUtils';

export const CheckLoginComponentWithKeyAndLayout = (
  props: React.JSX.IntrinsicAttributes & {
    children: React.ReactNode;
    notLoggedInChildren?: React.ReactNode;
    justChildren?: boolean | undefined;
  },
): ReactElement => {
  const { notLoggedInChildren, justChildren } = props;
  return (
    <CheckLoginComponentWithKey
      notLoggedInChildren={notLoggedInChildren}
      justChildren={justChildren}
    >
      {' '}
      {props.children}
    </CheckLoginComponentWithKey>
  );
};

export const CheckLoginComponentWithKey = (
  props: React.JSX.IntrinsicAttributes & {
    children: React.ReactNode;
    notLoggedInChildren?: React.ReactNode;
    justChildren?: boolean | undefined;
  },
) => {
  return <CheckLoginComponent {...props} initialiseAssetRead={true} />;
};

export function CheckLoginComponent({
  children,
  notLoggedInChildren,
  initialiseAssetRead,
  onAuthChange,
}: {
  children: React.ReactNode;
  notLoggedInChildren?: React.ReactNode;
  justChildren?: boolean;
  initialiseAssetRead?: boolean;
  onAuthChange?: () => void;
}) {
  const useAuthResult = useAuth0();

  const [hasSetAuthContext, setHasSetAuthContext] = useState(false);

  useEffect(() => {
    if (useAuthResult.isAuthenticated && useAuthResult.user && !hasSetAuthContext) {
      setAuthContextOnBackendService(useAuthResult);
      setHasSetAuthContext(true);
    }
  }, [useAuthResult, hasSetAuthContext]);

  const { isAuthenticated, isLoading, user, loginWithRedirect } = useAuthResult;
  const [baseData, setBaseData] = useState<CommonLoggedInData | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(true);
  const [hasTriedLogin, setHasTriedLogin] = useState(false);
  const location = useLocation();

  const [impersonationStatus, setImpersonationStatus] = useState<{
    impersonating: boolean;
    target_sub?: string;
    actor_email?: string;
    target_email?: string;
    expiresAt?: number;
  } | null>(null);
  const impersonationCheckDoneRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated || isLoading || impersonationCheckDoneRef.current) return;
    impersonationCheckDoneRef.current = true;

    new BackendService()
      .getImpersonationStatus()
      .then((status) => {
        console.info('[impersonation:web] CheckLogin impersonation probe', status);
        setImpersonationStatus({
          impersonating: status.impersonating === true,
          target_sub: status.target_sub,
          actor_email: status.actor_email,
          target_email: status.target_email,
          expiresAt: status.expiresAt,
        });
      })
      .catch(() => {
        setImpersonationStatus({ impersonating: false });
      });
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    console.log('Auth0 status:', {
      isLoading,
      isAuthenticated,
      user,
    });
  }, [isLoading, isAuthenticated, user]);

  const isImpersonating = impersonationStatus?.impersonating === true;

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && user) {
        const impl = new CommonLoggedInDataImpl(user.sub || 'irrelevant', null);
        impl.sidebarExpanded = sidebarExpanded;
        setBaseData(impl);

        if (initialiseAssetRead) {
          localStorage.removeItem('assetToken');
          new BackendService().setupAssetStorageWithRetry(true);
        }

        onAuthChange?.();
      } else if (isImpersonating && impersonationStatus?.target_sub) {
        const impl = new CommonLoggedInDataImpl(impersonationStatus.target_sub, null);
        impl.sidebarExpanded = sidebarExpanded;
        setBaseData(impl);

        if (initialiseAssetRead) {
          localStorage.removeItem('assetToken');
          new BackendService().setupAssetStorageWithRetry(true);
        }
      } else if (impersonationStatus !== null) {
        setBaseData(null);
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    sidebarExpanded,
    isImpersonating,
    impersonationStatus,
    initialiseAssetRead,
    onAuthChange,
  ]);

  useEffect(() => {
    const isPublicRoute = isNonAdminPublicRoute(location.pathname);

    if (impersonationStatus === null && !isAuthenticated && !isLoading) return;

    if (!isLoading && !isAuthenticated && !hasTriedLogin && !isPublicRoute && !isImpersonating) {
      console.info('[impersonation:web] CheckLogin auto-redirect to Auth0', {
        pathname: location.pathname,
        isPublicRoute,
        hasTriedLogin,
      });
      setHasTriedLogin(true);

      const redirectUri = buildAuthRedirectUri(
        location.pathname,
        location.search,
        location.hash,
        window.location.origin,
      );
      console.info('[impersonation:web] loginWithRedirect from CheckLogin', {
        forwardUrl: `${location.pathname}${location.search}${location.hash}`,
        redirectUri,
      });

      // E2E-only: lets a spec log in as a specific fixture/dynamically
      // -created identity (e.g. a new contact minted by
      // Auth0Service.registerLocalMockIdentity() during a referral
      // acceptance flow) by putting `?login_hint=<sub>` on the URL it
      // navigates to before this redirect fires. Real Auth0 only ever
      // treats login_hint as a UI pre-fill (never a bypass of credential
      // entry), but the mock OIDC provider's `interactiveLogin: false`
      // auto-approves whatever identity it maps to — so this is gated
      // behind the E2E build's product-name flag (never set outside
      // run-e2e-regression.js) to keep it fully inert everywhere else.
      const loginHint =
        process.env.REACT_APP_PRODUCT_NAME === 'Fides E2E'
          ? new URLSearchParams(location.search).get('login_hint')
          : null;

      loginWithRedirect({
        authorizationParams: {
          audience: process.env.REACT_APP_BACKEND_AUDIENCE,
          scope: 'openid profile email',
          redirect_uri: redirectUri,
          ...(loginHint ? { login_hint: loginHint } : {}),
        },
      });
    }
  }, [
    isLoading,
    isAuthenticated,
    hasTriedLogin,
    isImpersonating,
    impersonationStatus,
    location.pathname,
    location.search,
    location.hash,
    loginWithRedirect,
  ]);

  const contextValue = useMemo(() => {
    const wrapper = new CommonLoggedInDataWithToken(baseData);
    wrapper.sidebarExpanded = sidebarExpanded;
    wrapper.setSidebarExpandedCallback = (value: boolean) => {
      setSidebarExpanded(value);
    };
    return wrapper;
  }, [baseData, sidebarExpanded]);

  if (isLoading) {
    return (
      notLoggedInChildren || (
        <div>
          <CircularProgress />
        </div>
      )
    );
  }

  const hasContext = hasSetAuthContext || isImpersonating;
  if (!hasContext) {
    if (!isAuthenticated && impersonationStatus === null) {
      return notLoggedInChildren || <CircularProgress />;
    }
    if (!isImpersonating) {
      return notLoggedInChildren || <CircularProgress />;
    }
  }

  if (contextValue?.isLoggedIn) {
    return (
      <CommonLoggedInDataContext.Provider value={contextValue}>
        {children}
      </CommonLoggedInDataContext.Provider>
    );
  }

  if (!isAuthenticated && !isImpersonating) {
    return null;
  }

  return (
    notLoggedInChildren || (
      <div>
        <CircularProgress />
      </div>
    )
  );
}
